import type { Locale } from 'react-datepicker/dist/date_utils'
import type { IntlShape } from 'react-intl'
import { defineMessages } from 'react-intl'

type LocaleObj = Exclude<Locale, string>
type FormatMessage = IntlShape['formatMessage']

/**
 * Локали для react-datepicker
 * Заменяют тяжелые импорты из date-fns/locale
 */
const createSimpleLocale = (
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6,
  formatMessageFn: FormatMessage,
): LocaleObj => ({
  options: {
    weekStartsOn,
    firstWeekContainsDate: 1,
  },
  localize: {
    ordinalNumber: (n: number) => String(n),
    era: (n: number) =>
      n === 0
        ? formatMessageFn(datePickerMessages.eraBC)
        : formatMessageFn(datePickerMessages.eraAD),
    quarter: (n: number) =>
      formatMessageFn(datePickerMessages.quarter, { number: n + 1 }),
    month: (n: number) => {
      const monthMessages = [
        datePickerMessages.monthJan,
        datePickerMessages.monthFeb,
        datePickerMessages.monthMar,
        datePickerMessages.monthApr,
        datePickerMessages.monthMay,
        datePickerMessages.monthJun,
        datePickerMessages.monthJul,
        datePickerMessages.monthAug,
        datePickerMessages.monthSep,
        datePickerMessages.monthOct,
        datePickerMessages.monthNov,
        datePickerMessages.monthDec,
      ]
      return formatMessageFn(monthMessages[n] ?? datePickerMessages.monthJan)
    },
    day: (n: number) => {
      const dayMessages = [
        datePickerMessages.daySun,
        datePickerMessages.dayMon,
        datePickerMessages.dayTue,
        datePickerMessages.dayWed,
        datePickerMessages.dayThu,
        datePickerMessages.dayFri,
        datePickerMessages.daySat,
      ]
      return formatMessageFn(dayMessages[n] ?? datePickerMessages.daySun)
    },
    dayPeriod: (value: string) => {
      const n = value === 'AM' || value === 'am' || value === 'ДП' ? 0 : 1
      return n === 0
        ? formatMessageFn(datePickerMessages.dayPeriodAM)
        : formatMessageFn(datePickerMessages.dayPeriodPM)
    },
  },
  formatLong: {
    date: () => formatMessageFn(datePickerMessages.formatDate),
    time: () => formatMessageFn(datePickerMessages.formatTime),
    dateTime: () => formatMessageFn(datePickerMessages.formatDateTime),
  },
  match: {
    ordinalNumber: () => null,
    era: () => null,
    quarter: () => null,
    month: () => null,
    day: () => null,
    dayPeriod: () => null,
  },
})

export const createRuLocale = (formatMessage: FormatMessage) =>
  createSimpleLocale(1, formatMessage)

export const createEnUSLocale = (formatMessage: FormatMessage) =>
  createSimpleLocale(0, formatMessage)

const datePickerMessages = defineMessages({
  // Эры
  eraBC: {
    id: 'datePicker.era.bc',
    defaultMessage: 'до н.э.',
  },
  eraAD: {
    id: 'datePicker.era.ad',
    defaultMessage: 'н.э.',
  },

  // Кварталы
  quarter: {
    id: 'datePicker.quarter',
    defaultMessage: '{number} кв.',
  },

  // Месяцы
  monthJan: {
    id: 'datePicker.month.jan',
    defaultMessage: 'Янв',
  },
  monthFeb: {
    id: 'datePicker.month.feb',
    defaultMessage: 'Фев',
  },
  monthMar: {
    id: 'datePicker.month.mar',
    defaultMessage: 'Мар',
  },
  monthApr: {
    id: 'datePicker.month.apr',
    defaultMessage: 'Апр',
  },
  monthMay: {
    id: 'datePicker.month.may',
    defaultMessage: 'Май',
  },
  monthJun: {
    id: 'datePicker.month.jun',
    defaultMessage: 'Июн',
  },
  monthJul: {
    id: 'datePicker.month.jul',
    defaultMessage: 'Июл',
  },
  monthAug: {
    id: 'datePicker.month.aug',
    defaultMessage: 'Авг',
  },
  monthSep: {
    id: 'datePicker.month.sep',
    defaultMessage: 'Сен',
  },
  monthOct: {
    id: 'datePicker.month.oct',
    defaultMessage: 'Окт',
  },
  monthNov: {
    id: 'datePicker.month.nov',
    defaultMessage: 'Ноя',
  },
  monthDec: {
    id: 'datePicker.month.dec',
    defaultMessage: 'Дек',
  },

  // Дни недели
  daySun: {
    id: 'datePicker.day.sun',
    defaultMessage: 'Вс',
  },
  dayMon: {
    id: 'datePicker.day.mon',
    defaultMessage: 'Пн',
  },
  dayTue: {
    id: 'datePicker.day.tue',
    defaultMessage: 'Вт',
  },
  dayWed: {
    id: 'datePicker.day.wed',
    defaultMessage: 'Ср',
  },
  dayThu: {
    id: 'datePicker.day.thu',
    defaultMessage: 'Чт',
  },
  dayFri: {
    id: 'datePicker.day.fri',
    defaultMessage: 'Пт',
  },
  daySat: {
    id: 'datePicker.day.sat',
    defaultMessage: 'Сб',
  },

  // Периоды дня
  dayPeriodAM: {
    id: 'datePicker.dayPeriod.am',
    defaultMessage: 'ДП',
  },
  dayPeriodPM: {
    id: 'datePicker.dayPeriod.pm',
    defaultMessage: 'ПП',
  },

  // Форматы
  formatDate: {
    id: 'datePicker.format.date',
    defaultMessage: 'dd.MM.yyyy',
  },
  formatTime: {
    id: 'datePicker.format.time',
    defaultMessage: 'HH:mm',
  },
  formatDateTime: {
    id: 'datePicker.format.dateTime',
    defaultMessage: 'dd.MM.yyyy HH:mm',
  },
})
